import { useState, createContext, useEffect, useContext } from 'react';
import {
  useHistory,
  useParams,
} from 'react-router-dom/cjs/react-router-dom.min';
import api from '../../../Api';
import { initialConfig, initialIngestionMetaData } from '../constants';
import { ToastNotificationContext } from '../../context/ToastNotificationContext';

const ConfigurationContext = createContext({
  assignConnector: () => {},
  dataSource: localStorage.getItem('data_source') || '',
  numberOfSteps: 3,
  configuration: initialConfig,
  ingestionData: {
    uploadType: 'sample',
    uploadedFileName: '',
    uploadedFileLocation: '',
    feedType: '',
    contentType: '',
    volume_of_data: '',
    precedence: '',
    incremental_field: [],
    data_source_object:
      localStorage.getItem('data_source') === 'S3' ||
      localStorage.getItem('data_source') === 'GCS'
        ? localStorage.getItem('connector_name')
        : localStorage.getItem('data_source') === 'AZURE_BLOB'
        ? localStorage.getItem('container')
        : '',
    connector: localStorage.getItem('connector_id') || null,
    data_source_type: '',
    datashare_name:
      localStorage.getItem('data_source')?.toUpperCase() === 'REDSHIFT'
        ? localStorage.getItem('connector_name') || ''
        : '',
  },
  fetchingData: false,
  connectorName: localStorage.getItem('connector_name') || '',
});

const ConfigurationContextProvider = ({ children }) => {
  const { id } = useParams();
  const history = useHistory();
  const { setToastNotificationConfig } = useContext(ToastNotificationContext);

  const [dataSource, setDataSource] = useState(
    id ? '' : localStorage.getItem('data_source')?.toUpperCase() || ''
  );
  // On edit, avoid showing a stale mailbox from a previously selected connector.
  const [connectorName, setConnectorName] = useState(
    id ? '' : localStorage.getItem('connector_name') || ''
  );
  const [numberOfSteps, setNumberOfSteps] = useState(3);
  const [configuration, setConfiguration] = useState();
  const [ingestionData, setIngestionData] = useState(initialIngestionMetaData);
  const [fetchingData, setFetchingData] = useState(false);

  useEffect(() => {
    const fetchConfiguration = async () => {
      if (id) {
        try {
          setFetchingData(true);
          const res = await api.getConfiguration(id);
          const dataSourceType =
            res?.data?.data_source_type?.toUpperCase() || '';
          const connectorId = res?.data?.connector;

          setConfiguration(res?.data || {});
          setIngestionData({
            feed: res?.data?.feed,
            feedType: res?.data?.feed_type,
            uploadType: res?.data?.upload_type || '',
            uploadedFileLocation: res?.data?.uploaded_file_location || '',
            uploadedFileName: res?.data?.uploaded_file_name || '',
            metadata_source: Boolean(res?.data?.metadata_source),
            metadata_file_name: res?.data?.metadata_file_name || '',
            metadata_file_location: res?.data?.metadata_file_location || '',
            contentType: res?.data?.content_type || '',
            volume_of_data: res?.data?.volume_of_data || '',
            precedence: res?.data?.precedence || '',
            data_source_object: res?.data?.data_source_object,
            connector: connectorId,
            data_source_type: res?.data?.data_source_type,
            datashare_name: res?.data?.datashare_name,
            incremental_field: res?.data?.incremental_field || [],
            description: res?.data?.description,
            priority_processing: res?.data?.priority_processing || false,
          });
          setDataSource(dataSourceType);
          if (dataSourceType) {
            localStorage.setItem('data_source', dataSourceType);
          }

          // Resolve mailbox / bucket display name from the saved connector,
          // not stale localStorage from a previously selected connector.
          if (connectorId) {
            try {
              const connectorRes = await api.getConnectorDetails(connectorId);
              const resolvedName = connectorRes?.data?.connector_name || '';
              setConnectorName(resolvedName);
              localStorage.setItem('connector_name', resolvedName);
              localStorage.setItem('connector_id', String(connectorId));
              if (connectorRes?.data?.container != null) {
                localStorage.setItem('container', connectorRes.data.container);
              }
            } catch (connectorErr) {
              console.error(
                'ConfigurationContext: getConnectorDetails failed on edit',
                connectorErr
              );
            }
          }
        } catch (err) {
          console.error('ConfigurationContext: getConfiguration failed', err);
          const detail =
            err?.message ||
            err?.response?.data?.detail ||
            (typeof err?.response?.data === 'string'
              ? err.response.data
              : null);
          setToastNotificationConfig({
            open: true,
            kind: 'error',
            title: 'Error Fetching Configuration!',
            subtitle:
              detail ||
              'Network error, invalid JSON in the response body, or the request was rejected. Check Network + Console.',
          });
          history.push('/ingestion/dashboard');
        } finally {
          setFetchingData(false);
        }
      }
    };

    fetchConfiguration();
  }, [history, id]);

  // New-config only: hydrate from localStorage after connector selection.
  // Edit loads connector_name from the saved connector above — do not overwrite.
  useEffect(() => {
    if (id) {
      return;
    }

    const savedDataSource = localStorage.getItem('data_source');
    const savedConnectorName = localStorage.getItem('connector_name');
    const savedConnectorId = localStorage.getItem('connector_id');
    const savedContainer = localStorage.getItem('container');

    if (savedDataSource) {
      setDataSource(savedDataSource);
      setConnectorName(savedConnectorName);

      setIngestionData(prevData => ({
        ...prevData,
        data_source_type: savedDataSource,
        connector: savedConnectorId,
        ...(savedDataSource === 'S3' || savedDataSource === 'GCS'
          ? { data_source_object: savedConnectorName, datashare_name: '' }
          : savedDataSource == 'AZURE_BLOB'
          ? { data_source_object: savedContainer, datashare_name: '' }
          : savedDataSource === 'Redshift'
          ? { datashare_name: savedConnectorName, data_source_object: '' }
          : {}),
      }));
    }
  }, [id]);

  const assignConnector = id => {
    const getConnector = async () => {
      try {
        setFetchingData(true);
        const res = await api.getConnectorDetails(id);
        const dataSource = res.data?.data_source_type?.toUpperCase();
        localStorage.setItem('data_source', dataSource);
        setDataSource(dataSource);

        const connectorName = res?.data?.connector_name;
        const connectorId = res?.data?.connector_id;
        localStorage.setItem('connector_name', connectorName);
        localStorage.setItem('connector_id', connectorId);
        localStorage.setItem('container', res?.data?.container);
        setConnectorName(connectorName);

        const newIngestionData = {
          ...ingestionData,
          data_source_type: dataSource,
          connector: connectorId,
        };

        if (
          dataSource === 'S3' ||
          dataSource === 'GCS' ||
          dataSource == 'AZURE_BLOB'
        ) {
          newIngestionData.data_source_object =
            dataSource == 'AZURE_BLOB' ? res?.data?.container : connectorName;
          newIngestionData.datashare_name = '';
        } else if (dataSource === 'MFT') {
          // Bucket is company_folder; mailbox is connector_name — resolved in MFTSourceConfig.
          newIngestionData.data_source_object = '';
          newIngestionData.datashare_name = '';
        } else if (dataSource?.toUpperCase() === 'REDSHIFT') {
          newIngestionData.datashare_name = connectorName;
          newIngestionData.data_source_object = '';
        }

        setIngestionData(newIngestionData);
      } catch (error) {
        setToastNotificationConfig({
          open: true,
          kind: 'error',
          title: 'Error Fetching Connector!',
          subtitle: 'Error in fetching connector details. Please try again.',
        });
      } finally {
        setFetchingData(false);
      }
    };
    if (id === 'MFU') {
      setDataSource('MFU');
      localStorage.setItem('data_source', 'MFU');
    } else {
      getConnector();
    }
  };

  const value = {
    dataSource,
    numberOfSteps,
    assignConnector,
    configuration,
    setConfiguration,
    ingestionData,
    setIngestionData,
    fetchingData,
    connectorName,
  };
  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
};

export { ConfigurationContext, ConfigurationContextProvider };
